import { t as translate } from "../preferences";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  PackagePlus,
  RefreshCw,
  Square,
  SquareCheckBig,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  type ClientDetails,
  type ProductOut,
  type ServiceOut,
  type WorkCreateInput,
  type WorkCreateResult,
} from "../api";
import { useAuth } from "../auth";
import { PageTitle } from "../components/Cards";
import { useMechanicBack } from "../components/AppShell";
import { InlineError, InitialState, makeUuid, messageOf, money, useResource } from "../components/DataState";
import { haptic } from "../haptic";
import { useLocalBack } from "../navigation";
import { formatUzPhone } from "../../../phone";
import { BarcodeProductPicker } from "../components/BarcodeProductPicker";
import { QuantityStepper, shouldRemoveOnDecrement } from "../components/QuantityStepper";
import { quantityNumber, stepQuantity } from "../quantity";
import {
  clearWorkDraft,
  markWorkHistoryBoundary,
  readWorkDraft,
  remainingDraftMs,
  writeWorkDraft,
  type WorkDraft,
} from "../workDraft";
import {
  integerMoney,
  paymentLines as getPaymentLines,
  resolvePayment,
  togglePaymentMethod,
  workTotal,
} from "../workPayment";
import { saveConfirmation } from "../confirmationState";
import { cleanIntegerInput, formatIntegerInput } from "../numberFormat";

type WorkTarget = {
  clientId: number;
  clientName: string;
  clientPhone: string;
  carId: number;
  plateNumber: string;
  carModel: string | null;
};

function WorkHeader({ target }: { target?: Pick<WorkDraft, "clientName" | "plateNumber" | "carModel"> | null }) {
  const goBack = useMechanicBack();
  return <div className="m-app-safe-top m-sticky m-top-0 m-z-40 -m-mx-4 m-mb-4 m-flex m-items-center m-gap-2 m-border-b m-border-border m-bg-background/95 m-px-3 m-pb-2 m-backdrop-blur">
    <button type="button" className="m-touch-icon m-shrink-0" aria-label={translate("Ortga")} onClick={goBack}><ChevronLeft className="m-size-6" /></button>
    {target ? <div className="m-min-w-0 m-flex-1"><h1 className="m-truncate m-text-lg m-font-black">{target.clientName}</h1><p className="m-truncate m-text-sm m-text-muted"><span className="m-uppercase">{target.plateNumber}</span>{target.carModel ? ` · ${target.carModel}` : ""}</p></div> : null}
  </div>;
}

function serviceReminderText(
  service: NonNullable<WorkDraft["services"]>[number],
  draft: WorkDraft,
  catalog: ServiceOut[],
): string {
  if (typeof service.reminderText === "string") return service.reminderText;
  // A manually edited draft from the former combined-field UI used the same
  // override for every reminder. Preserve that transient draft behavior.
  if (draft.reminderMode === "override" && draft.reminderEdited) return draft.reminderNote;
  return catalog.find((item) => item.id === service.serviceId)?.sms_text?.trim() ?? "";
}

export type WorkConfirmationState = {
  result: WorkCreateResult;
  clientId: number;
  clientName: string;
  plateNumber: string;
  serviceName: string;
  total: number;
};

function cleanMoney(value: string): string {
  return value.replace(/\D/g, "");
}

function paymentMethodLabel(method: WorkDraft["paymentMethod"]): string {
  if (method === "naqd") return translate("Naqd");
  if (method === "terminal") return translate("Terminal (karta)");
  if (method === "karta_otkazma") return translate("Kartaga o‘tkazma");
  return translate("Bank hisobiga");
}

function ambiguous(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || error.status === 408 || error.status >= 500);
}

function reconciliationMustContinue(error: unknown, outcomeUnknown: boolean): boolean {
  if (ambiguous(error)) return true;
  return outcomeUnknown && error instanceof ApiError && [401, 402, 403].includes(error.status);
}

async function sendWithTimeout(payload: WorkCreateInput): Promise<WorkCreateResult> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 20_000);
  try {
    return await api.createWork(payload, controller.signal);
  } finally {
    window.clearTimeout(timer);
  }
}

function targetFrom(client: ClientDetails, carId: number): WorkTarget | null {
  const car = client.cars.find((item) => item.id === carId);
  if (!car) return null;
  return {
    clientId: client.id,
    clientName: client.full_name,
    clientPhone: client.phone,
    carId: car.id,
    plateNumber: car.plate_number,
    carModel: car.model,
  };
}

export function WorkScreen() {
  const auth = useAuth();
  if (auth.state.status !== "authenticated") throw new Error("WorkScreen requires authentication");
  const user = auth.state.user;
  const workshopId = user.workshop_id ?? 0;
  const owner = user.role === "workshop_owner";
  const canAttachParts = owner || user.can_be_sotuvchi;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRead = useRef(readWorkDraft(user.id, workshopId));
  const [draft, setDraft] = useState<WorkDraft | null>(initialRead.current.draft);
  const [mode, setMode] = useState<"gate" | "select" | "edit">(
    initialRead.current.draft?.pendingAttempt
      ? "edit"
      : initialRead.current.draft
        ? "gate"
        : "select",
  );
  const [pendingTarget, setPendingTarget] = useState<WorkTarget | null>(null);
  const [persistWarning, setPersistWarning] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "unknown">(
    initialRead.current.draft?.pendingAttempt ? "unknown" : "idle",
  );
  const [productPicker, setProductPicker] = useState(false);
  const [servicePicker, setServicePicker] = useState(false);
  const servicePickerRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [paymentPicker, setPaymentPicker] = useState(false);
  const paymentPickerRef = useRef<HTMLDivElement>(null);
  const allowNavigation = useRef(false);
  const queryHandled = useRef(false);
  const recoverOnMount = useRef(Boolean(initialRead.current.draft?.pendingAttempt));

  const resource = useResource(async () => {
    const [services, clients, tariff] = await Promise.all([
      api.services(),
      api.clients(),
      auth.access?.tariff ? Promise.resolve(auth.access.tariff) : api.tariff(),
    ]);
    const hasProducts = tariff.features.includes("tovars");
    const products = canAttachParts && hasProducts ? await api.products() : [];
    return { services, clients, tariff, products };
  }, [user.id, canAttachParts]);
  const data = resource.data;
  // Partial payment and the debt ledger are available on every tariff.
  const hasDebts = true;
  const partsAllowed = canAttachParts && Boolean(data?.tariff.features.includes("tovars"));
  const locked = saveState !== "idle" || Boolean(draft?.pendingAttempt);

  useLayoutEffect(() => {
    const node = noteRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.max(80, node.scrollHeight)}px`;
  }, [mode, draft?.note]);

  useEffect(() => {
    if (!servicePicker) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!servicePickerRef.current?.contains(event.target as Node)) setServicePicker(false);
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    return () => document.removeEventListener("pointerdown", dismissOutside, true);
  }, [servicePicker]);

  useEffect(() => {
    if (!paymentPicker) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!paymentPickerRef.current?.contains(event.target as Node)) setPaymentPicker(false);
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    return () => document.removeEventListener("pointerdown", dismissOutside, true);
  }, [paymentPicker]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    Boolean(draft) && !allowNavigation.current && currentLocation.pathname !== nextLocation.pathname,
  );
  useLocalBack(productPicker, () => setProductPicker(false));
  useLocalBack(blocker.state === "blocked", () => { if (blocker.state === "blocked") blocker.reset(); });

  const store = useCallback((next: WorkDraft) => {
    setDraft(next);
    if (!writeWorkDraft(next)) {
      setPersistWarning(translate("Qoralamani qurilmada saqlab bo‘lmadi. Sahifani yangilamang."));
    } else {
      setPersistWarning(null);
    }
  }, []);

  const update = useCallback((patch: Partial<WorkDraft>) => {
    setDraft((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      if (!writeWorkDraft(next)) setPersistWarning(translate("Qoralamani qurilmada saqlab bo‘lmadi. Sahifani yangilamang."));
      else setPersistWarning(null);
      return next;
    });
  }, []);

  const createDraft = useCallback((target: WorkTarget) => {
    const next: WorkDraft = {
      version: 1,
      userId: user.id,
      workshopId,
      createdAt: Date.now(),
      operationUuid: makeUuid(),
      ...target,
      serviceId: null,
      serviceName: "",
      price: "",
      services: [],
      parts: [],
      note: "",
      reminderNote: "",
      reminderMode: "override",
      reminderEdited: false,
      paid: "",
      debtDueDays: "",
      paymentMethod: "naqd",
      payments: [{ method: "naqd", amount: "" }],
    };
    store(next);
    setSaveError(null);
    setSaveState("idle");
    setPendingTarget(null);
    setMode("edit");
  }, [store, user.id, workshopId]);

  useEffect(() => {
    if (!draft) return;
    if (draft.pendingAttempt) return;
    const wait = remainingDraftMs(draft);
    if (wait <= 0) {
      clearWorkDraft(user.id);
      setDraft(null);
      allowNavigation.current = true;
      navigate("/mechanic/", { replace: true, state: { notice: translate("8 soatlik ish qoralamasi muddati tugadi.") } });
      return;
    }
    const timer = window.setTimeout(() => {
      clearWorkDraft(user.id);
      setDraft(null);
      allowNavigation.current = true;
      navigate("/mechanic/", { replace: true, state: { notice: translate("8 soatlik ish qoralamasi muddati tugadi.") } });
    }, wait);
    return () => window.clearTimeout(timer);
  }, [draft?.createdAt, Boolean(draft?.pendingAttempt), navigate, user.id]);

  useEffect(() => {
    if (!data || queryHandled.current) return;
    const clientId = Number(params.get("clientId"));
    const carId = Number(params.get("carId"));
    if (!Number.isInteger(clientId) || clientId <= 0) {
      queryHandled.current = true;
      return;
    }
    const client = data.clients.find((item) => item.id === clientId);
    if (!client) {
      setSaveError(translate("Tanlangan mijoz ustaxonada topilmadi."));
      queryHandled.current = true;
      return;
    }
    if (!Number.isInteger(carId) || carId <= 0) {
      setMode("select");
      queryHandled.current = true;
      return;
    }
    const target = targetFrom(client, carId);
    if (!target) {
      setSaveError(translate("Tanlangan avtomobil mijozga tegishli emas."));
    } else if (!draft) {
      createDraft(target);
    } else if (draft.pendingAttempt) {
      // Never expose draft replacement while an earlier request may already
      // have committed, even if a different vehicle arrives via deep link.
      setMode("edit");
    } else if (draft.carId === target.carId) {
      setMode("edit");
    } else {
      setPendingTarget(target);
      setMode("gate");
    }
    queryHandled.current = true;
  }, [createDraft, data, draft, params]);

  useEffect(() => {
    if (!data || !draft?.pendingAttempt || !recoverOnMount.current) return;
    recoverOnMount.current = false;
    // A persisted in-flight request may have committed before the page closed.
    // Record that fact before making any recovery request.
    const recoveredDraft: WorkDraft = {
      ...draft,
      pendingAttempt: { ...draft.pendingAttempt, outcomeUnknown: true },
    };
    store(recoveredDraft);
    if (recoveredDraft.pendingAttempt!.replayUsed) {
      setSaveState("unknown");
      setSaveError(translate("Oldingi saqlash natijasi noma’lum. Shu operatsiyani xavfsiz qayta tekshiring."));
      return;
    }
    void submitAttempt(recoveredDraft, true);
    // Only a pending operation found during hydration is auto-reconciled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, draft?.operationUuid]);

  function selectTarget(target: WorkTarget) {
    if (draft?.pendingAttempt) {
      setMode("edit");
      return;
    }
    if (draft && draft.carId !== target.carId) {
      setPendingTarget(target);
      setMode("gate");
      return;
    }
    if (draft) setMode("edit");
    else createDraft(target);
  }

  function discardAndContinue() {
    if (draft?.pendingAttempt) {
      setMode("edit");
      return;
    }
    clearWorkDraft(user.id);
    setDraft(null);
    if (pendingTarget) createDraft(pendingTarget);
    else setMode("select");
  }

  function validateAndBuild(): WorkCreateInput | null {
    if (!draft || !data) return null;
    const selectedServices = draft.services ?? (
      draft.serviceId && draft.serviceName
        ? [{ serviceId: draft.serviceId, serviceName: draft.serviceName, price: draft.price }]
        : []
    );
    if (selectedServices.length === 0) {
      setSaveError(translate("Xizmatni tanlang."));
      return null;
    }
    if (selectedServices.some((item) => integerMoney(item.price) === null)) {
      setSaveError(translate("Xizmat narxini to‘g‘ri kiriting."));
      return null;
    }
    if (selectedServices.some((item) => serviceReminderText(item, draft, data.services).length > 500)) {
      setSaveError(translate("Eslatma 500 belgidan oshmasligi kerak."));
      return null;
    }
    if (draft.parts.length && !partsAllowed) {
      setSaveError(translate("Bu tarif yoki vazifa bilan ehtiyot qism qo‘shib bo‘lmaydi."));
      return null;
    }
    if (draft.parts.some((part) => quantityNumber(part.quantity) <= 0 || integerMoney(part.price) === null)) {
      setSaveError(translate("Ehtiyot qismlar miqdori va narxini tekshiring."));
      return null;
    }
    const payment = resolvePayment(draft, hasDebts);
    if (payment.error) {
      setSaveError(payment.error);
      return null;
    }
    setSaveError(null);
    return {
      car_id: draft.carId,
      service_id: selectedServices[0].serviceId,
      services: selectedServices.map((item) => ({
        service_id: item.serviceId,
        price: item.price,
        ...(draft.reminderMode === "override" ? {
          reminder_text_override: serviceReminderText(item, draft, data.services).trim() || null,
        } : {}),
      })),
      user_id: user.id,
      price: String(selectedServices.reduce((sum, item) => sum + (integerMoney(item.price) ?? 0), 0)),
      note: draft.note.trim() || null,
      reminder_note: draft.reminderMode === "override" ? null : draft.reminderNote.trim() || null,
      parts: draft.parts.map((part) => ({
        product_id: part.productId,
        miqdor: part.quantity,
        sotuv_narx: part.price,
      })),
      client_uuid: draft.operationUuid,
      ...payment.fields,
    };
  }

  async function complete(result: WorkCreateResult, source: WorkDraft) {
    clearWorkDraft(user.id);
    setDraft(null);
    allowNavigation.current = true;
    haptic("success");
    markWorkHistoryBoundary();
    const confirmationState = {
      result,
      clientId: source.clientId,
      clientName: source.clientName,
      plateNumber: source.plateNumber,
      serviceName: source.serviceName,
      total: workTotal(source),
    } satisfies WorkConfirmationState;
    // Replace the editable entry with Home, then put confirmation on top. The
    // browser and Telegram Back target is Home rather than the old work flow.
    navigate("/mechanic/", { replace: true });
    navigate("/mechanic/work/confirmation", { state: { confirmationId: saveConfirmation(user, confirmationState) } });
  }

  async function submitAttempt(attemptDraft: WorkDraft, replayAllowed: boolean) {
    const pending = attemptDraft.pendingAttempt;
    if (!pending) return;
    setSaveState("saving");
    setSaveError(null);
    try {
      const result = await sendWithTimeout(pending.payload);
      await complete(result, attemptDraft);
    } catch (error) {
      if (ambiguous(error) && replayAllowed && !pending.replayUsed) {
        const replayDraft = {
          ...attemptDraft,
          pendingAttempt: { ...pending, replayUsed: true, outcomeUnknown: true },
        };
        store(replayDraft);
        try {
          const result = await sendWithTimeout(replayDraft.pendingAttempt.payload);
          await complete(result, replayDraft);
          return;
        } catch (replayError) {
          if (reconciliationMustContinue(replayError, true)) {
            setSaveState("unknown");
            setSaveError(translate("Saqlash natijasi noma’lum. Takrorlamasdan tekshirish uchun shu operatsiyani qayta yuboring."));
            haptic("warning");
            return;
          }
          update({ pendingAttempt: undefined });
          setSaveState("idle");
          setSaveError(messageOf(replayError));
          haptic("error");
          return;
        }
      }
      if (reconciliationMustContinue(error, Boolean(pending.outcomeUnknown))) {
        if (!pending.outcomeUnknown) {
          store({
            ...attemptDraft,
            pendingAttempt: { ...pending, outcomeUnknown: true },
          });
        }
        setSaveState("unknown");
        setSaveError(translate("Saqlash natijasi noma’lum. Shu operatsiyani xavfsiz qayta tekshiring."));
        haptic("warning");
      } else {
        update({ pendingAttempt: undefined });
        setSaveState("idle");
        setSaveError(messageOf(error));
        haptic("error");
      }
    }
  }

  function save() {
    if (!draft || saveState === "saving") return;
    if (draft.pendingAttempt) {
      void submitAttempt(draft, false);
      return;
    }
    const payload = validateAndBuild();
    if (!payload) return;
    const next = {
      ...draft,
      pendingAttempt: { payload, replayUsed: false, outcomeUnknown: false },
    };
    store(next);
    void submitAttempt(next, true);
  }


  function addProduct(product: ProductOut) {
    if (!draft || locked) return;
    const existing = draft.parts.find((part) => part.productId === product.id);
    const parts = existing
      ? draft.parts.map((part) => part.productId === product.id ? { ...part, quantity: stepQuantity(part.quantity, 1) } : part)
      : [...draft.parts, {
          productId: product.id,
          name: product.nomi,
          unit: product.birlik,
          quantity: "1",
          price: String(Math.round(Number(product.sotuv_narx))),
        }];
    update({ parts });
    setProductPicker(false);
  }

  function setPart(productId: number, patch: { quantity?: string; price?: string }) {
    if (!draft || locked) return;
    const parts = draft.parts.map((part) => part.productId === productId ? { ...part, ...patch } : part);
    update({ parts });
  }

  function removePart(productId: number) {
    if (!draft || locked) return;
    update({ parts: draft.parts.filter((part) => part.productId !== productId) });
  }

  function toggleService(service: ServiceOut) {
    if (!draft || locked) return;
    const current = draft.services ?? (
      draft.serviceId && draft.serviceName
        ? [{ serviceId: draft.serviceId, serviceName: draft.serviceName, price: draft.price }]
        : []
    );
    const services = current.some((item) => item.serviceId === service.id)
      ? current.filter((item) => item.serviceId !== service.id)
      : [...current, {
          serviceId: service.id,
          serviceName: service.name,
          price: String(Math.round(Number(service.narx ?? 0))),
          reminderText: service.sms_text?.trim() ?? "",
        }];
    const first = services[0];
    update({
      services,
      serviceId: first?.serviceId ?? null,
      serviceName: services.map((item) => item.serviceName).join(', '),
      price: String(services.reduce((sum, item) => sum + (integerMoney(item.price) ?? 0), 0)),
    });
  }

  function setServicePrice(serviceId: number, price: string) {
    if (!draft || locked) return;
    const services = (draft.services ?? []).map((item) =>
      item.serviceId === serviceId ? { ...item, price } : item
    );
    update({
      services,
      price: String(services.reduce((sum, item) => sum + (integerMoney(item.price) ?? 0), 0)),
    });
  }

  function setServiceReminder(serviceId: number, reminderText: string) {
    if (!draft || !data || locked) return;
    const services = (draft.services ?? []).map((item) => ({
      ...item,
      reminderText: item.serviceId === serviceId
        ? reminderText
        : serviceReminderText(item, draft, data.services),
    }));
    update({
      services,
      reminderNote: "",
      reminderMode: "override",
      reminderEdited: true,
    });
  }

  function setPaymentLines(lines: NonNullable<WorkDraft["payments"]>) {
    if (!draft || locked) return;
    const paid = lines.length === 1
      ? lines[0].amount
      : String(lines.reduce((sum, line) => sum + (integerMoney(line.amount) ?? 0), 0));
    update({
      payments: lines,
      paymentMethod: lines[0]?.method ?? draft.paymentMethod,
      paid,
    });
  }

  function toggleWorkPayment(method: WorkDraft["paymentMethod"]) {
    if (!draft || locked) return;
    setPaymentLines(togglePaymentMethod(getPaymentLines(draft), method, workTotal(draft)));
  }

  function confirmExit() {
    if (saveState === "saving" || draft?.pendingAttempt) return;
    clearWorkDraft(user.id);
    setDraft(null);
    allowNavigation.current = true;
    if (blocker.state === "blocked") blocker.proceed();
  }

  if (mode === "gate" && draft) {
    return <div className="m-space-y-4">
      <WorkHeader target={draft} />
      <PageTitle title={translate("Saqlangan qoralama")} description={translate("Bir qurilmada bitta faol ish qoralamasi saqlanadi.")} />
      <section className="m-rounded-2xl m-border m-border-border m-bg-surface m-p-4 m-shadow-card">
        <p className="m-font-black m-uppercase">{draft.plateNumber}</p>
        <p className="m-mt-1 m-text-sm m-text-muted">{draft.clientName}{draft.serviceName ? ` · ${draft.serviceName}` : ""}</p>
      </section>
      {pendingTarget ? <p className="m-rounded-xl m-bg-elevated m-p-3 m-text-sm">{translate("Yangi tanlov:")}<strong className="m-uppercase">{pendingTarget.plateNumber}</strong>{translate(". Davom etish eski qoralamani o‘chiradi.")}</p> : null}
      <button className="m-primary-button" onClick={() => setMode("edit")}>{translate("Qoralamani davom ettirish")}</button>
      {!draft.pendingAttempt ? <button className="m-secondary-button" onClick={discardAndContinue}><Trash2 className="m-size-5" />{translate("Qoralamani o‘chirib yangi ish")}</button> : null}
    </div>;
  }

  if (mode === "select") {
    const requestedClient = Number(params.get("clientId"));
    const clients = data?.clients.filter((client) => !Number.isInteger(requestedClient) || requestedClient <= 0 || client.id === requestedClient) ?? [];
    return <div>
      <WorkHeader />
      <InlineError message={saveError || resource.error} onRetry={resource.error ? () => void resource.retry() : undefined} />
      <InitialState loading={resource.loading} error={resource.error} empty={Boolean(data && clients.length === 0)} onRetry={() => void resource.retry()}>
        {data ? <div className="m-space-y-3">{clients.map((client) => <section key={client.id} className="m-rounded-2xl m-border m-border-border m-bg-surface m-p-4">
          <p className="m-font-bold">{client.full_name}</p><p className="m-text-sm m-text-muted">{formatUzPhone(client.phone)}</p>
          <div className="m-mt-3 m-space-y-2">{client.cars.map((car) => <button key={car.id} className="m-flex m-min-h-14 m-w-full m-items-center m-justify-between m-gap-3 m-rounded-xl m-bg-elevated m-px-3 m-text-left" onClick={() => selectTarget(targetFrom(client, car.id)!)}><span><strong className="m-block m-uppercase">{car.plate_number}</strong><span className="m-text-sm m-text-muted">{car.model || translate("Model kiritilmagan")}</span></span><ChevronRight className="m-size-5 m-text-primary" /></button>)}</div>
        </section>)}</div> : null}
      </InitialState>
    </div>;
  }

  if (!draft) return null;
  const selectedServices = draft.services ?? (
    draft.serviceId && draft.serviceName
      ? [{ serviceId: draft.serviceId, serviceName: draft.serviceName, price: draft.price }]
      : []
  );
  const total = workTotal(draft);
  const payment = resolvePayment(draft, hasDebts);
  const selectedPayments = getPaymentLines(draft);
  return <div className="m-space-y-5">
    <WorkHeader target={draft} />
    <InlineError message={persistWarning} />
    <InlineError message={resource.error} onRetry={() => void resource.retry()} />
    <InlineError message={saveError} />
    {resource.loading && !data ? <p className="m-rounded-xl m-bg-elevated m-p-3 m-text-sm m-text-muted">{translate("Xizmat va tarif ma’lumotlari yuklanmoqda…")}</p> : null}
    {locked ? <div className="m-rounded-xl m-bg-elevated m-p-3 m-text-sm" role="status">{saveState === "saving" ? translate("Ish saqlanmoqda…") : translate("Natija aniqlanmaguncha maydonlar o‘zgartirilmaydi.")}</div> : null}

    <section className="m-space-y-4 m-rounded-2xl m-border m-border-border m-bg-surface m-p-4">
      <div ref={servicePickerRef} onBlurCapture={(event) => {
        const next = event.relatedTarget as Node | null;
        if (!next || !event.currentTarget.contains(next)) setServicePicker(false);
      }}><span className="m-field-label">{translate("Xizmat")}</span><div className="m-overflow-hidden m-rounded-xl m-border m-border-border m-bg-elevated">
        {selectedServices.map((service) => <div key={service.serviceId} className="m-flex m-min-h-14 m-items-center m-gap-2 m-border-b m-border-border m-px-3 m-py-2">
          <span className="m-min-w-0 m-flex-1 m-truncate m-font-bold">{service.serviceName}</span>
          <input className="m-text-input m-w-28 m-text-right" aria-label={translate(`${service.serviceName} narxi`)} inputMode="numeric" disabled={locked} value={formatIntegerInput(service.price)} onChange={(event) => setServicePrice(service.serviceId, cleanMoney(event.target.value))} />
          <button className="m-touch-icon" aria-label={translate(`${service.serviceName}ni oвЂchirish`)} disabled={locked} onClick={() => {
            const option = data?.services.find((item) => item.id === service.serviceId);
            if (option) toggleService(option);
          }}><Trash2 className="m-size-5" /></button>
        </div>)}
        <button type="button" className="m-flex m-min-h-12 m-w-full m-items-center m-justify-between m-px-3 m-text-left m-text-muted" disabled={locked || !data} aria-label={translate("Xizmatni tanlang")} aria-expanded={servicePicker} aria-controls="work-services-options" onClick={() => { setPaymentPicker(false); setServicePicker((open) => !open); }}>
          <span>{translate("Xizmatni tanlang")}</span>{servicePicker
            ? <ChevronUp aria-hidden="true" data-testid="work-services-chevron-up" className="m-size-5" />
            : <ChevronDown aria-hidden="true" data-testid="work-services-chevron-down" className="m-size-5" />}
        </button>
      </div>
      {servicePicker ? <div id="work-services-options" role="listbox" aria-multiselectable="true" className="m-mt-1 m-max-h-60 m-overflow-y-auto m-rounded-xl m-border m-border-border m-bg-surface">
        {data?.services.map((service) => {
          const checked = selectedServices.some((item) => item.serviceId === service.id);
          const Mark = checked ? SquareCheckBig : Square;
          return <button type="button" key={service.id} role="checkbox" aria-checked={checked} className="m-flex m-min-h-12 m-w-full m-items-center m-gap-2 m-border-b m-border-border m-px-3 m-text-left" onClick={() => toggleService(service)}>
            <Mark className="m-size-5 m-text-primary" /><span>{service.name}</span>
          </button>;
        })}
      </div> : null}</div>
      <div className="m-grid m-gap-2">{selectedServices.map((service, index) => {
        const reminderText = serviceReminderText(service, draft, data?.services ?? []);
        const label = `${translate("Mijoz uchun eslatma")}: ${service.serviceName}`;
        return <label key={service.serviceId}>{index === 0 ? <span className="m-field-label">{translate("Mijoz uchun eslatma")}</span> : null}<textarea className="m-text-input m-min-h-24 m-py-3" aria-label={label} maxLength={500} disabled={locked} value={reminderText} onChange={(event) => setServiceReminder(service.serviceId, event.target.value)} /><span className={`m-mt-1 m-block m-text-right m-text-xs ${reminderText.length > 500 ? "m-text-danger" : "m-text-muted"}`}>{reminderText.length}/500</span>{reminderText.length > 500 ? <span className="m-mt-1 m-block m-text-sm m-text-danger" role="alert">{translate("Eslatma 500 belgidan oshmasligi kerak.")}</span> : null}</label>;
      })}</div>
      <label><span className="m-field-label">{translate("Ichki izoh")}</span><textarea ref={noteRef} rows={2} className="m-text-input m-min-h-20 m-resize-none m-overflow-hidden m-py-3" aria-label={translate("Ichki izoh")} disabled={locked} value={draft.note} onChange={(event) => update({ note: event.target.value })} /></label>
    </section>

    {partsAllowed ? <section className="m-space-y-3 m-rounded-2xl m-border m-border-border m-bg-surface m-p-4">
      <div className="m-flex m-items-center m-justify-between m-gap-2"><h2 className="m-font-black">{translate("Ehtiyot qismlar")}</h2><button className="m-flex m-min-h-11 m-items-center m-gap-1 m-rounded-xl m-px-3 m-font-bold m-text-primary" disabled={locked} onClick={() => setProductPicker(true)}><PackagePlus className="m-size-5" />{translate("Qo‘shish")}</button></div>
      {draft.parts.length === 0 ? <p className="m-rounded-xl m-bg-elevated m-p-3 m-text-sm m-text-muted">{translate("Ehtiyot qism qo‘shilmagan.")}</p> : null}
      {draft.parts.map((part) => <article key={part.productId} className="m-rounded-xl m-bg-elevated m-p-3">
        <div className="m-flex m-items-start m-justify-between m-gap-2"><div><p className="m-font-bold">{part.name}</p><p className="m-text-sm m-text-muted">{part.unit}</p></div><button className="m-touch-icon" aria-label={translate(`${part.name}ni o‘chirish`, `Удалить ${part.name}`)} disabled={locked} onClick={() => removePart(part.productId)}><Trash2 className="m-size-5" /></button></div>
        <div className="m-mt-3 m-grid m-grid-cols-[minmax(0,1fr)_minmax(0,1fr)] m-items-end m-gap-3"><QuantityStepper label={translate("Miqdor")} value={part.quantity} disabled={locked} onChange={(quantity) => setPart(part.productId, { quantity })} onDecrement={() => shouldRemoveOnDecrement(part.quantity) ? removePart(part.productId) : setPart(part.productId, { quantity: stepQuantity(part.quantity, -1) })} /><label><span className="m-field-label">{translate("Sotuv narxi")}</span><input className="m-text-input" aria-label={translate(`${part.name} narxi`, `Цена ${part.name}`)} inputMode="numeric" disabled={locked} value={formatIntegerInput(part.price)} onChange={(event) => setPart(part.productId, { price: cleanMoney(event.target.value) })} /></label></div>
      </article>)}
    </section> : null}

    <section className="m-space-y-4 m-rounded-2xl m-border m-border-border m-bg-surface m-p-4">
      <div className="m-flex m-items-center m-justify-between m-gap-3"><span className="m-font-bold">{translate("Jami")}</span><strong className="m-text-xl m-tabular-nums">{money(total)}</strong></div>
      {hasDebts ? <>
        <div ref={paymentPickerRef} onBlurCapture={(event) => {
          const next = event.relatedTarget as Node | null;
          if (!next || !event.currentTarget.contains(next)) setPaymentPicker(false);
        }}>
          <span className="m-field-label">{translate("To‘lov usuli")}</span>
          <button
            type="button"
            className="m-flex m-min-h-12 m-w-full m-items-center m-justify-between m-gap-2 m-rounded-xl m-border m-border-border m-bg-elevated m-px-3 m-text-left"
            disabled={locked}
            aria-label={translate("To‘lov usulini tanlang")}
            aria-expanded={paymentPicker}
            aria-controls="work-payment-options"
            onClick={() => { setServicePicker(false); setPaymentPicker((open) => !open); }}
          >
            <span className="m-min-w-0 m-flex-1 m-truncate">
              {selectedPayments.length
                ? selectedPayments.map((line) => paymentMethodLabel(line.method)).join(", ")
                : translate("Tanlanmagan")}
            </span>
            {paymentPicker
              ? <ChevronUp aria-hidden="true" data-testid="work-payments-chevron-up" className="m-size-5" />
              : <ChevronDown aria-hidden="true" data-testid="work-payments-chevron-down" className="m-size-5" />}
          </button>
          {paymentPicker ? <div id="work-payment-options" role="listbox" aria-multiselectable="true" className="m-mt-1 m-max-h-60 m-overflow-y-auto m-rounded-xl m-border m-border-border m-bg-surface">
            {(["naqd", "terminal", "karta_otkazma", "bank"] as const).map((method) => {
              const checked = selectedPayments.some((line) => line.method === method);
              const Mark = checked ? SquareCheckBig : Square;
              return <button type="button" key={method} role="checkbox" aria-checked={checked} className="m-flex m-min-h-12 m-w-full m-cursor-pointer m-items-center m-gap-2 m-border-b m-border-border m-px-3 m-text-left" onClick={() => toggleWorkPayment(method)}>
                <Mark className="m-size-5 m-text-primary" /><span>{paymentMethodLabel(method)}</span>
              </button>;
            })}
          </div> : null}
        </div>
        {selectedPayments.map((line) => {
          const label = selectedPayments.length === 1
            ? translate("To‘langan summa")
            : translate(`To‘langan summa: ${paymentMethodLabel(line.method)}`);
          return <label key={line.method}><span className="m-field-label">{label}</span><input className="m-text-input" aria-label={label} inputMode="numeric" disabled={locked} value={formatIntegerInput(selectedPayments.length === 1 && line.amount === "" && !line.amountEdited ? String(total) : line.amount)} onChange={(event) => setPaymentLines(selectedPayments.map((item) => item.method === line.method ? { ...item, amount: cleanMoney(event.target.value), amountEdited: true } : item))} placeholder={selectedPayments.length === 1 ? "" : "0"} /></label>;
        })}
        <div className="m-flex m-items-center m-justify-between m-rounded-xl m-bg-elevated m-p-3"><span className="m-text-sm m-text-muted">{translate("Qarz")}</span><strong className="m-tabular-nums">{money(payment.debt)}</strong></div>
        {payment.debt > 0 ? <label><span className="m-field-label">{translate("Qarz muddati (kun)")}</span><input className="m-text-input" aria-label={translate("Qarz muddati")} inputMode="numeric" disabled={locked} value={formatIntegerInput(draft.debtDueDays)} onChange={(event) => update({ debtDueDays: cleanIntegerInput(event.target.value) })} /></label> : null}
      </> : null}
    </section>

    <button className="m-primary-button" disabled={saveState === "saving" || !data} onClick={save}>{saveState === "saving" ? <RefreshCw className="m-size-5 m-animate-spin" /> : draft.pendingAttempt ? <RefreshCw className="m-size-5" /> : <Check className="m-size-5" />}{draft.pendingAttempt ? translate("Natijani qayta tekshirish") : translate("Ishni saqlash")}</button>

    {productPicker ? <div className="m-fixed m-inset-0 m-z-[70] m-flex m-items-end m-bg-black/40 m-app-safe-bottom"><div className="m-max-h-[82vh] m-w-full m-overflow-y-auto m-rounded-t-2xl m-bg-surface m-p-4"><div className="m-mb-3 m-flex m-items-center m-justify-between"><h2 className="m-text-lg m-font-black">{translate("Ehtiyot qism tanlang")}</h2><button className="m-touch-icon m-rounded-full m-bg-danger m-text-white" onClick={() => setProductPicker(false)} aria-label={translate("Tanlovni yopish")}><X className="m-size-5" /></button></div><BarcodeProductPicker products={data?.products ?? []} onPick={addProduct} requireStock searchLabel={translate("Ehtiyot qism qidirish")} /></div></div> : null}

    {blocker.state === "blocked" ? <div className="m-fixed m-inset-0 m-z-[80] m-flex m-items-center m-justify-center m-bg-black/45 m-p-4"><section className="m-w-full m-max-w-sm m-rounded-2xl m-bg-surface m-p-5 m-shadow-card" role="dialog" aria-modal="true" aria-labelledby="discard-title"><span className="m-flex m-size-12 m-items-center m-justify-center m-rounded-xl m-bg-elevated m-text-warning"><AlertTriangle className="m-size-6" /></span><h2 id="discard-title" className="m-mt-4 m-text-xl m-font-black">{locked ? translate("Saqlash natijasi tekshirilmoqda") : translate("Kiritilganlar o‘chadi")}</h2><p className="m-mt-2 m-text-sm m-leading-6 m-text-muted">{locked ? translate("Takroriy ish yaratilmasligi uchun avval shu saqlash natijasini aniqlang.") : translate("Chiqishni tasdiqlasangiz ushbu ish qoralamasi o‘chiriladi.")}</p><div className="m-mt-5 m-space-y-2"><button className="m-primary-button" onClick={() => blocker.reset()}>{translate("Ishda qolish")}</button>{!locked ? <button className="m-secondary-button" onClick={confirmExit}>{translate("Chiqish va o‘chirish")}</button> : null}</div></section></div> : null}
  </div>;
}
